// Live-audio -> viseme engine shared by the talking-head puppets (web). Heuristic, no ML.
// ---- Tunables -------------------------------------------------------------------------------
const SILENCE_RMS = 0.012; // below this: silence
const OPEN_LO = 0.02; // smoothstep(OPEN_LO, OPEN_HI, rms) = openness
const OPEN_HI = 0.14;
const AH_OPEN_AT = 0.7; // openness above this -> AH_open instead of AA
const FRICATIVE_RATIO = 0.55; // high-band / (low+high) above this -> FF/TH
const CENTROID_LOW = 1100; // Hz, below -> OO/OH
const CENTROID_HIGH = 2300; // Hz, above -> EE
const LOW_BAND_MAX = 1000; // Hz
const HIGH_BAND_MIN = 3500; // Hz
const DIP_RATIO = 0.4; // rms < DIP_RATIO * recent peak (while speaking) -> MM
const PEAK_DECAY_PER_S = 0.5; // recent peak decays by this factor per second
const RMS_SMOOTH = 0.5; // EMA weight of the new rms sample
const MM_HOLD_MS = 90; // MM held on the way down to silence
const MIN_HOLD_MS = 60; // minimum time a viseme stays
export const CROSSFADE_MS = 70;
export const FEATHER = 0.12; // fraction of the crop edge that fades
const ONSET_RATIO = 1.6; // rms jump (vs previous frame) that counts as a consonant release
const ALT_MS = 170; // alternation period for OO/OH, AA/AH_open, FF/TH

export interface VisemeFeatures {
  rms: number;
  openness: number; // 0..1
  centroid: number; // Hz
  highRatio: number; // 0..1
  dip: boolean; // energy dipped sharply (between syllables)
  alt: boolean; // slow toggle for picking between near-equivalent shapes
  speaking: boolean;
  phase?: number; // slow counter for cycling near-equivalent shapes
  onset?: boolean; // energy just jumped (consonant release)
}

/** Heuristic audio features -> viseme name (18-shape set; missing shapes fall back via visemeOrFallback).
 *  Replace with an ML model later; keep the signature. */
export function pickViseme(f: VisemeFeatures): string {
  if (!f.speaking || f.rms < SILENCE_RMS) return 'sil';
  const phase = f.phase ?? (f.alt ? 0 : 1);
  if (f.dip) return phase % 3 === 2 ? 'NN' : 'MM';
  if (f.highRatio > FRICATIVE_RATIO) return (['SS', 'FF', 'CH', 'TH'] as const)[phase % 4];
  if (f.onset) return f.centroid > CENTROID_HIGH ? 'DD' : f.centroid < CENTROID_LOW ? 'KK' : (['DD', 'LL', 'RR'] as const)[phase % 3];
  if (f.centroid < CENTROID_LOW) return phase % 2 ? 'OH' : 'OO';
  if (f.centroid > CENTROID_HIGH) return phase % 2 ? 'IH' : 'EE';
  if (f.openness > AH_OPEN_AT) return 'AH_open';
  return (['AA', 'EH', 'AA', 'AH_open'] as const)[phase % 4];
}

/** Nearest available shape when a deity's bank lacks one (Clio has the original 10). */
const FALLBACK: Record<string, string> = { IH: 'EE', EH: 'AA', SS: 'EE', CH: 'OO', DD: 'EE', KK: 'AA', NN: 'MM', RR: 'OO' };
export function visemeOrFallback<T>(name: string, bank: Record<string, T>): T | undefined {
  return bank[name] ?? bank[FALLBACK[name] ?? ''] ?? bank.sil;
}
// ---------------------------------------------------------------------------------------------


const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
export { smoothstep };

export interface VisemeState { cur: string; prev: string; fadeT: number }

/** Stateful analyser: call step(now) every animation frame; returns current/previous shape + crossfade 0..1. */
export function makeVisemeEngine(getAnalyser: () => { an: AnalyserNode; ctx: AudioContext } | null, isSpeaking: () => boolean) {
  const analyserRef = { get current() { return getAnalyser(); } };
  const speakingRef = { get current() { return isSpeaking(); } };
    // Analysis + viseme state
  const timeBuf = new Uint8Array(1024);
  let freqBuf: Uint8Array<ArrayBuffer> | null = null;
  let rmsS = 0;
  let peak = 0;
  let lastT = performance.now();
  let cur = 'sil';
  let prev = 'sil';
  let curSince = 0;
  let fadeStart = 0;
  let belowSince = 0;

  const analyse = (now: number): string => {
    const dt = Math.max(0.001, (now - lastT) / 1000);
    lastT = now;
    const a = analyserRef.current;
    const speakingNow = speakingRef.current;
    let rms = 0;
    let centroid = 0;
    let highRatio = 0;
    if (a && speakingNow) {
      a.an.getByteTimeDomainData(timeBuf);
      let s = 0;
      for (let i = 0; i < timeBuf.length; i++) {
        const x = (timeBuf[i] - 128) / 128;
        s += x * x;
      }
      rms = Math.sqrt(s / timeBuf.length);
      if (!freqBuf || freqBuf.length !== a.an.frequencyBinCount) freqBuf = new Uint8Array(a.an.frequencyBinCount);
      a.an.getByteFrequencyData(freqBuf);
      const binHz = a.ctx.sampleRate / a.an.fftSize;
      let num = 0, den = 0, low = 0, high = 0;
      for (let i = 1; i < freqBuf.length; i++) {
        const f = i * binHz;
        const m = freqBuf[i] / 255;
        const p = m * m;
        num += f * p;
        den += p;
        if (f < LOW_BAND_MAX) low += p;
        else if (f >= HIGH_BAND_MIN) high += p;
      }
      centroid = den > 0 ? num / den : 0;
      highRatio = low + high > 0 ? high / (low + high) : 0;
    }
    const prevRms = rmsS;
    rmsS = rmsS + (rms - rmsS) * RMS_SMOOTH;
    // onset: energy jumps out of a dip (consonant release) -> brief D/K/L/R shape
    const onset = speakingNow && prevRms < peak * DIP_RATIO * 1.2 && rmsS > prevRms * ONSET_RATIO && rmsS >= SILENCE_RMS;
    peak = Math.max(rmsS, peak * Math.pow(PEAK_DECAY_PER_S, dt));
    const dip = speakingNow && rmsS >= SILENCE_RMS && rmsS < peak * DIP_RATIO;
    let want = pickViseme({
      rms: rmsS,
      openness: smoothstep(OPEN_LO, OPEN_HI, rmsS),
      centroid,
      highRatio,
      dip,
      alt: Math.floor(now / ALT_MS) % 2 === 0,
      phase: Math.floor(now / ALT_MS),
      onset,
      speaking: speakingNow,
    });
    // Brief MM on the way down to silence, so closing lips don't snap shut.
    if (want === 'sil' && speakingNow && cur !== 'sil' && cur !== 'MM') {
      if (!belowSince) belowSince = now;
      if (now - belowSince < MM_HOLD_MS) want = 'MM';
    } else if (want !== 'sil') belowSince = 0;
    if (!speakingNow) want = 'sil';
    return want;
  };


  return {
    step(now: number): VisemeState {
      const want = analyse(now);
      if (want !== cur && (now - curSince >= MIN_HOLD_MS || !speakingRef.current)) {
        prev = cur;
        cur = want;
        curSince = now;
        fadeStart = now;
      }
      return { cur, prev, fadeT: Math.min(1, (now - fadeStart) / CROSSFADE_MS) };
    },
  };
}

/** Crop with soft-rectangle alpha edges (computed once per viseme). */
export function feathered(im: HTMLImageElement): HTMLCanvasElement {
  const w = im.naturalWidth;
  const h = im.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const x = c.getContext('2d')!;
  x.drawImage(im, 0, 0);
  const img = x.getImageData(0, 0, w, h);
  const fx = Math.max(1, w * FEATHER);
  const fy = Math.max(1, h * FEATHER);
  for (let j = 0; j < h; j++) {
    const ay = smoothstep(0, 1, Math.min(j + 0.5, h - j - 0.5) / fy);
    for (let i = 0; i < w; i++) {
      const ax = smoothstep(0, 1, Math.min(i + 0.5, w - i - 0.5) / fx);
      const k = (j * w + i) * 4 + 3;
      img.data[k] = Math.round(img.data[k] * ax * ay);
    }
  }
  x.putImageData(img, 0, 0);
  return c;
}

/** Silent analyser tap on a remote audio track (playback stays on its own element). */
export function tapAnalyser(track: MediaStreamTrack): { an: AnalyserNode; ctx: AudioContext; close: () => void } | null {
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  const ctx = new AC();
  const src = ctx.createMediaStreamSource(new MediaStream([track]));
  const an = ctx.createAnalyser();
  an.fftSize = 1024;
  an.smoothingTimeConstant = 0.3;
  const zero = ctx.createGain();
  zero.gain.value = 0;
  src.connect(an);
  an.connect(zero);
  zero.connect(ctx.destination);
  void ctx.resume().catch(() => {});
  return {
    an,
    ctx,
    close: () => {
      try { src.disconnect(); an.disconnect(); zero.disconnect(); } catch { /* already gone */ }
      void ctx.close().catch(() => {});
    },
  };
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = src;
  });
}
